import { useEffect, useReducer } from 'react';
import { ApolloLink, Observable } from 'apollo-link';
import type { Operation, FetchResult } from 'apollo-link';

type OperationError = {
  networkError: Error;
  operation: Operation;
};

type NetworkStatus = {
  numPendingQueries: number;
  queryError?: OperationError;
};

type NetworkStatusAction =
  | { type: 'CANCEL' }
  | { type: 'ERROR'; networkError: Error; operation: Operation }
  | { type: 'REQUEST' }
  | { type: 'SUCCESS' };

const listeners = new Set<(action: NetworkStatusAction) => void>();

function notify(action: NetworkStatusAction): void {
  listeners.forEach((listener) => listener(action));
}

function networkStatusReducer(state: NetworkStatus, action: NetworkStatusAction): NetworkStatus {
  switch (action.type) {
    case 'REQUEST':
      return { ...state, numPendingQueries: state.numPendingQueries + 1, queryError: undefined };
    case 'SUCCESS':
    case 'CANCEL':
      return { ...state, numPendingQueries: Math.max(state.numPendingQueries - 1, 0) };
    case 'ERROR':
      return {
        numPendingQueries: Math.max(state.numPendingQueries - 1, 0),
        queryError: { networkError: action.networkError, operation: action.operation },
      };
    default:
      return state;
  }
}

function isQuery(operation: Operation): boolean {
  const definition = operation.query.definitions.find((def) => def.kind === 'OperationDefinition');
  return definition?.kind === 'OperationDefinition' && definition.operation === 'query';
}

export const networkStatusLink = new ApolloLink((operation: Operation, forward) => {
  if (!isQuery(operation)) {
    return forward(operation);
  }

  notify({ type: 'REQUEST' });

  const forwarded = forward(operation);

  return new Observable<FetchResult>((observer) => {
    let settled = false;

    const subscription = forwarded.subscribe({
      next: (result) => {
        if (!settled) {
          settled = true;
          notify({ type: 'SUCCESS' });
        }
        observer.next(result);
      },
      error: (error) => {
        settled = true;
        notify({ type: 'ERROR', networkError: error, operation });
        observer.error(error);
      },
      complete: () => observer.complete(),
    });

    return () => {
      if (!settled) {
        notify({ type: 'CANCEL' });
      }
      subscription.unsubscribe();
    };
  });
});

/** Subscribes to network status changes dispatched by networkStatusLink. */
export function useApolloNetworkStatus(): NetworkStatus {
  const [state, dispatch] = useReducer(networkStatusReducer, {
    numPendingQueries: 0,
    queryError: undefined,
  });

  useEffect(() => {
    let frameId: number;

    const listener = (action: NetworkStatusAction): void => {
      frameId = requestAnimationFrame(() => dispatch(action));
    };

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return state;
}
